/**
 * ============================================================
 * HOBBLIVISION TEXT REGION DETECTOR
 * ============================================================
 *
 * Finds likely text-bearing areas INSIDE the detector ROI.
 *
 * This is deliberately a cheap, browser-native localization pass.
 * It does NOT perform OCR and it does NOT attempt to identify text.
 * Its job is simply to answer:
 *
 *   "Where is there enough text-like structure that OCR is worth
 *    spending time on?"
 *
 * Design goals:
 * - no OpenCV dependency
 * - inspect the whole ROI, not fixed top/middle/bottom slices
 * - detect horizontal and vertical text
 * - tolerate labels/logos and text embedded in card faces
 * - cap the number of OCR regions
 * - return rich diagnostics for the Inspector
 * ============================================================
 */

const VERSION = "1.0";

const CONFIG = {
    MAX_ANALYSIS_SIZE: 1000,
    MIN_COMPONENT_AREA: 0.0008,
    MAX_COMPONENT_AREA: 0.22,
    MIN_REGION_WIDTH: 28,
    MIN_REGION_HEIGHT: 14,
    MAX_REGIONS: 4,
    DILATION_PASSES: 2,
    EDGE_PERCENTILE_FLOOR: 82,
    EDGE_THRESHOLD_MIN: 18,
    TEXT_PADDING_X: 0.035,
    TEXT_PADDING_Y: 0.08,
    MERGE_GAP_X: 0.025,
    MERGE_GAP_Y: 0.025,
    MIN_TEXTNESS: 0.18,
};

export function detectTextRegionsFromROI(roiImage, options = {}) {
    const started = performance.now();

    if (!roiImage?.width || !roiImage?.height) {
        return createEmptyResult("invalid-roi");
    }

    const config = { ...CONFIG, ...options };
    const sourceWidth = roiImage.width;
    const sourceHeight = roiImage.height;

    const analysis = createAnalysisCanvas(
        roiImage,
        config.MAX_ANALYSIS_SIZE
    );

    if (!analysis) {
        return createEmptyResult("analysis-canvas-unavailable");
    }

    const { canvas, scale } = analysis;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
        return createEmptyResult("analysis-context-unavailable");
    }

    const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const gray = toGrayscale(image.data, canvas.width, canvas.height);
    const edge = buildEdgeMap(gray, canvas.width, canvas.height);

    const threshold = chooseEdgeThreshold(edge, config);
    const binary = thresholdEdges(edge, threshold);
    const dilated = dilate(binary, canvas.width, canvas.height, config.DILATION_PASSES);

    let components = connectedComponents(
        dilated,
        edge,
        canvas.width,
        canvas.height,
        config
    );

    components = mergeNearbyRegions(components, canvas.width, canvas.height, config);
    components = scoreAndRank(components, edge, canvas.width, canvas.height, config);

    const selected = components
        .slice(0, config.MAX_REGIONS)
        .map((component, index) => {
            const padded = addPadding(
                component,
                canvas.width,
                canvas.height,
                config
            );

            return {
                id: `text-${index + 1}`,
                type: classifyOrientation(padded),
                orientation: orientationFor(padded),
                confidence: Number(component.confidence.toFixed(3)),
                textness: Number(component.textness.toFixed(3)),
                source: {
                    x: Math.round(padded.x / scale),
                    y: Math.round(padded.y / scale),
                    width: Math.round(padded.width / scale),
                    height: Math.round(padded.height / scale),
                },
                normalized: {
                    x: Number((padded.x / canvas.width).toFixed(5)),
                    y: Number((padded.y / canvas.height).toFixed(5)),
                    width: Number((padded.width / canvas.width).toFixed(5)),
                    height: Number((padded.height / canvas.height).toFixed(5)),
                },
            };
        });

    return {
        version: VERSION,
        detected: selected.length > 0,
        regions: selected,
        diagnostics: {
            analysisWidth: canvas.width,
            analysisHeight: canvas.height,
            analysisScale: scale,
            edgeThreshold: Number(threshold.toFixed(2)),
            rawComponentCount: components.length,
            selectedCount: selected.length,
            processingMs: Number((performance.now() - started).toFixed(0)),
        },
    };
}

function createEmptyResult(reason) {
    return {
        version: VERSION,
        detected: false,
        regions: [],
        diagnostics: {
            reason,
            analysisWidth: 0,
            analysisHeight: 0,
            analysisScale: 1,
            edgeThreshold: 0,
            rawComponentCount: 0,
            selectedCount: 0,
            processingMs: 0,
        },
    };
}

function createAnalysisCanvas(source, maxSize) {
    const scale = Math.min(1, maxSize / Math.max(source.width, source.height));
    const width = Math.max(1, Math.round(source.width * scale));
    const height = Math.max(1, Math.round(source.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "medium";
    ctx.drawImage(source, 0, 0, width, height);
    return { canvas, scale };
}

function toGrayscale(data, width, height) {
    const gray = new Uint8Array(width * height);
    for (let i = 0, p = 0; i < gray.length; i++, p += 4) {
        gray[i] = Math.round(
            0.299 * data[p] +
            0.587 * data[p + 1] +
            0.114 * data[p + 2]
        );
    }
    return gray;
}

function buildEdgeMap(gray, width, height) {
    const edge = new Uint8Array(width * height);
    for (let y = 1; y < height - 1; y++) {
        const row = y * width;
        for (let x = 1; x < width - 1; x++) {
            const i = row + x;
            const gx = Math.abs(gray[i + 1] - gray[i - 1]);
            const gy = Math.abs(gray[i + width] - gray[i - width]);
            edge[i] = Math.min(255, gx + gy);
        }
    }
    return edge;
}

function chooseEdgeThreshold(edge, config) {
    const values = [];
    const step = Math.max(1, Math.floor(edge.length / 20000));
    for (let i = 0; i < edge.length; i += step) values.push(edge[i]);
    values.sort((a, b) => a - b);
    const percentileIndex = Math.floor(
        values.length * (config.EDGE_PERCENTILE_FLOOR / 100)
    );
    return Math.max(
        config.EDGE_THRESHOLD_MIN,
        values[Math.min(values.length - 1, percentileIndex)] || config.EDGE_THRESHOLD_MIN
    );
}

function thresholdEdges(edge, threshold) {
    const binary = new Uint8Array(edge.length);
    for (let i = 0; i < edge.length; i++) {
        binary[i] = edge[i] >= threshold ? 1 : 0;
    }
    return binary;
}

function dilate(source, width, height, passes) {
    let current = source;
    for (let pass = 0; pass < passes; pass++) {
        const next = new Uint8Array(current.length);
        for (let y = 1; y < height - 1; y++) {
            for (let x = 1; x < width - 1; x++) {
                const i = y * width + x;
                if (
                    current[i] ||
                    current[i - 1] || current[i + 1] ||
                    current[i - width] || current[i + width]
                ) {
                    next[i] = 1;
                }
            }
        }
        current = next;
    }
    return current;
}

function connectedComponents(binary, edge, width, height, config) {
    const visited = new Uint8Array(binary.length);
    const components = [];
    const minArea = width * height * config.MIN_COMPONENT_AREA;
    const maxArea = width * height * config.MAX_COMPONENT_AREA;

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const start = y * width + x;
            if (!binary[start] || visited[start]) continue;

            const queue = [start];
            visited[start] = 1;
            let head = 0;
            let minX = x, maxX = x, minY = y, maxY = y;
            let pixels = 0;
            let edgeSum = 0;

            while (head < queue.length) {
                const index = queue[head++];
                const px = index % width;
                const py = Math.floor(index / width);
                pixels++;
                edgeSum += edge[index];
                minX = Math.min(minX, px);
                maxX = Math.max(maxX, px);
                minY = Math.min(minY, py);
                maxY = Math.max(maxY, py);

                const neighbors = [index - 1, index + 1, index - width, index + width];
                for (const n of neighbors) {
                    if (n < 0 || n >= binary.length || visited[n] || !binary[n]) continue;
                    const nx = n % width;
                    const ny = Math.floor(n / width);
                    if (Math.abs(nx - px) > 1 || Math.abs(ny - py) > 1) continue;
                    visited[n] = 1;
                    queue.push(n);
                }
            }

            const regionWidth = maxX - minX + 1;
            const regionHeight = maxY - minY + 1;
            const area = regionWidth * regionHeight;
            if (
                pixels < minArea ||
                area > maxArea ||
                regionWidth < config.MIN_REGION_WIDTH ||
                regionHeight < config.MIN_REGION_HEIGHT
            ) continue;

            const density = pixels / Math.max(1, area);
            const averageEdge = edgeSum / Math.max(1, pixels);
            const aspect = regionWidth / Math.max(1, regionHeight);

            components.push({
                x: minX,
                y: minY,
                width: regionWidth,
                height: regionHeight,
                area,
                density,
                averageEdge,
                aspect,
                textness: calculateTextness({ density, averageEdge, aspect }),
                confidence: 0,
            });
        }
    }
    return components;
}

function calculateTextness(component) {
    const densityScore = Math.min(1, component.density * 2.8);
    const edgeScore = Math.min(1, component.averageEdge / 90);
    const orientationScore =
        component.aspect >= 1.15 || component.aspect <= 0.87 ? 1 : 0.72;
    return densityScore * 0.35 + edgeScore * 0.45 + orientationScore * 0.20;
}

function mergeNearbyRegions(regions, width, height, config) {
    const result = [...regions];
    let changed = true;
    while (changed) {
        changed = false;
        outer: for (let i = 0; i < result.length; i++) {
            for (let j = i + 1; j < result.length; j++) {
                if (!shouldMerge(result[i], result[j], width, height, config)) continue;
                result[i] = union(result[i], result[j]);
                result.splice(j, 1);
                changed = true;
                break outer;
            }
        }
    }
    return result;
}

function shouldMerge(a, b, width, height, config) {
    const gapX = Math.max(0, Math.max(a.x, b.x) - Math.min(a.x + a.width, b.x + b.width));
    const gapY = Math.max(0, Math.max(a.y, b.y) - Math.min(a.y + a.height, b.y + b.height));
    const overlapX = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
    const overlapY = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
    const nearHorizontal = gapX <= width * config.MERGE_GAP_X && overlapY > Math.min(a.height, b.height) * 0.15;
    const nearVertical = gapY <= height * config.MERGE_GAP_Y && overlapX > Math.min(a.width, b.width) * 0.15;
    return nearHorizontal || nearVertical;
}

function union(a, b) {
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    const right = Math.max(a.x + a.width, b.x + b.width);
    const bottom = Math.max(a.y + a.height, b.y + b.height);
    const width = right - x;
    const height = bottom - y;
    const area = width * height;
    const density = (a.density * a.area + b.density * b.area) / Math.max(1, a.area + b.area);
    const averageEdge = (a.averageEdge + b.averageEdge) / 2;
    const aspect = width / Math.max(1, height);
    return {
        ...a,
        x, y, width, height, area, density, averageEdge, aspect,
        textness: calculateTextness({ density, averageEdge, aspect }),
    };
}

function scoreAndRank(regions, edge, width, height, config) {
    return regions
        .map(region => {
            const areaFraction = region.area / (width * height);
            const sizeScore = Math.min(1, areaFraction / 0.06);
            const textScore = region.textness;
            const aspectScore =
                region.aspect >= 1.3 || region.aspect <= 0.75 ? 1 : 0.72;
            const locationScore =
                region.y < height * 0.30 || region.y > height * 0.70 ? 1 : 0.82;
            region.confidence =
                textScore * 0.58 +
                aspectScore * 0.18 +
                sizeScore * 0.12 +
                locationScore * 0.12;
            return region;
        })
        .filter(region => region.textness >= config.MIN_TEXTNESS)
        .sort((a, b) => b.confidence - a.confidence);
}

function addPadding(region, width, height, config) {
    const padX = Math.max(8, width * config.TEXT_PADDING_X);
    const padY = Math.max(6, height * config.TEXT_PADDING_Y);
    const x = Math.max(0, region.x - padX);
    const y = Math.max(0, region.y - padY);
    const right = Math.min(width, region.x + region.width + padX);
    const bottom = Math.min(height, region.y + region.height + padY);
    return {
        ...region,
        x,
        y,
        width: right - x,
        height: bottom - y,
    };
}

function classifyOrientation(region) {
    if (region.width / Math.max(1, region.height) >= 1.35) return "horizontal";
    if (region.height / Math.max(1, region.width) >= 1.35) return "vertical";
    return "square_or_compact";
}

function orientationFor(region) {
    const type = classifyOrientation(region);
    return type === "vertical" ? 90 : 0;
}
